import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { apiScopes } from '@/constants/product'

type Phase = 'sending' | 'ok'

interface ApiPanelProps {
    className?: string
    expanded?: boolean
}

/** A small monospace request card that sends, then answers 200 OK. Key scopes cycle underneath. */
export const ApiPanel: FC<ApiPanelProps> = ({ className, expanded = false }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref, { threshold: 0.3 })
    const reduced = useReducedMotion()
    const [phase, setPhase] = useState<Phase>('ok')
    const [scope, setScope] = useState(1)

    useEffect(() => {
        if (reduced || !inView) return

        const timer = window.setTimeout(() => {
            if (phase === 'ok') {
                setPhase('sending')
                setScope(value => (value + 1) % apiScopes.length)
            }
            else {
                setPhase('ok')
            }
        }, phase === 'ok' ? 4200 : 1000)

        return () => window.clearTimeout(timer)
    }, [phase, inView, reduced])

    return (
        <div ref={ref} className={cn('space-y-3', className)}>
            <div className='overflow-hidden rounded-[18px] border border-white/[0.06] bg-charcoal font-mono text-[12px] leading-[1.7] text-white/80 shadow-[0_20px_40px_-24px_rgba(0,0,0,0.6)]'>
                <div className='flex items-center gap-1.5 border-b border-white/[0.06] px-4 py-2.5'>
                    <span className='size-2 rounded-full bg-white/15' />
                    <span className='size-2 rounded-full bg-white/15' />
                    <span className='size-2 rounded-full bg-white/15' />
                    <span className='ml-2 text-[10.5px] uppercase tracking-[0.14em] text-white/35'>
                        api.quayside.dev
                    </span>
                </div>

                <pre className='overflow-x-auto px-4 py-3 whitespace-pre'>
                    <span className='text-[#9fb4ff]'>POST</span>
                    {' /v2/flows/run\n'}
                    <span className='text-white/40'>{'Authorization: Bearer qs_live_••••7f3a\n'}</span>
                    {'{\n'}
                    {'  '}<span className='text-[#ffc7b0]'>&quot;flow&quot;</span>{': '}<span className='text-[#cfe6ff]'>&quot;bookings-to-erp&quot;</span>{',\n'}
                    {'  '}<span className='text-[#ffc7b0]'>&quot;lane&quot;</span>{': '}<span className='text-[#cfe6ff]'>&quot;RTM-CHI&quot;</span>{',\n'}
                    {'  '}<span className='text-[#ffc7b0]'>&quot;booking&quot;</span>{': '}<span className='text-[#cfe6ff]'>&quot;QS-48213&quot;</span>{'\n'}
                    {'}'}
                </pre>

                <div className='flex items-center justify-between border-t border-white/[0.06] px-4 py-2.5'>
                    {phase === 'sending' ? (
                        <span className='flex items-center gap-2 text-white/50'>
                            <span className='size-3 animate-spin rounded-full border-[1.5px] border-white/20 border-t-white/70' />
                            Sending
                        </span>
                    ) : (
                        <span className='flex items-center gap-2 text-[#5fe39a] animate-fade-soft'>
                            <span className='size-1.5 rounded-full bg-[#5fe39a]' />
                            200 OK
                        </span>
                    )}

                    <span className={cn('text-white/35 transition-opacity', phase === 'ok' ? 'opacity-100' : 'opacity-0')}>
                        142 ms
                    </span>
                </div>

                {expanded && (
                    <pre className={cn('overflow-x-auto border-t border-white/[0.06] px-4 py-3 whitespace-pre text-white/60 transition-opacity duration-500', phase === 'ok' ? 'opacity-100' : 'opacity-30')}>
                        {'{\n'}
                        {'  '}<span className='text-[#ffc7b0]'>&quot;status&quot;</span>{': '}<span className='text-[#cfe6ff]'>&quot;delivered&quot;</span>{',\n'}
                        {'  '}<span className='text-[#ffc7b0]'>&quot;erp_order&quot;</span>{': '}<span className='text-[#cfe6ff]'>&quot;PO-118204&quot;</span>{',\n'}
                        {'  '}<span className='text-[#ffc7b0]'>&quot;eta&quot;</span>{': '}<span className='text-[#cfe6ff]'>&quot;2026-10-23T09:00Z&quot;</span>{'\n'}
                        {'}'}
                    </pre>
                )}
            </div>

            <div className='flex flex-wrap items-center gap-1.5'>
                <span className='eyebrow mr-1 text-stone'>
                    Key scope
                </span>

                {apiScopes.map((item, index) => (
                    <span
                        key={item}
                        className={cn(
                            'rounded-full border px-2.5 py-1 font-mono text-[11px] transition-colors duration-500',
                            index === scope ? 'border-ultramarine/30 bg-ultramarine-soft text-ultramarine' : 'border-ink/10 text-stone'
                        )}
                    >
                        {item}
                    </span>
                ))}
            </div>
        </div>
    )
}
